import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreCollectionLayout();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const isCollection = this.closest('collection-component') !== null;
    if (isCollection && mediaQueryLarge.matches) value = 'default';
    if (!mediaQueryLarge.matches && value !== 'default' && value !== 'mobile-single') return;

    if (isCollection && !mediaQueryLarge.matches) {
      // Persist before the transition so a filter/sort morph that lands while
      // animation work is pending restores the user's just-selected layout.
      sessionStorage.setItem('product-grid-view-mobile', value);
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      if (!isCollection) {
        sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      }
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #restoreCollectionLayout() {
    if (this.closest('collection-component') === null) return;

    const value = mediaQueryLarge.matches
      ? 'default'
      : sessionStorage.getItem('product-grid-view-mobile') === 'mobile-single'
        ? 'mobile-single'
        : 'default';
    const option = this.querySelector(`input[name="grid-mobile"][value="${value}"]`);
    if (option instanceof HTMLInputElement) option.checked = true;
    this.#setLayout(value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isCollection = this.closest('collection-component') !== null;
    let targetValue = 'default';

    if (isCollection && !event.matches) {
      const storedMobile = sessionStorage.getItem('product-grid-view-mobile');
      targetValue = storedMobile === 'mobile-single' ? 'mobile-single' : 'default';
    }

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector(`input[name="grid-mobile"][value="${targetValue}"]`);

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout(isCollection ? targetValue : 'default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
